import { useState, useEffect, SyntheticEvent } from 'react';
import { useListener } from '@/hooks/use-listener';
import { Widget } from '@/components/widget';
import { useObjectJson } from '@/hooks/use-object-json';
import { Editor } from '@monaco-editor/react';
import { Button } from '@/components/button';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { LoadingCircle } from '@/components/loading-circle';
import { useTheme } from '@/hooks/use-theme';

const monacoEditorOptions = {
  minimap: {
    enabled: false,
  },
  automaticLayout: true,
  readOnly: true,
};

export function ObjectJsonWidget({ id = 'object-json' }: { id?: string }) {
  const [uuid, setUuid] = useState<string>('');
  const [container, setContainer] = useState<string>('');
  const [refetchInterval, setRefetchInterval] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<number>(0);
  const { data } = useObjectJson(uuid, container, { refetchInterval });
  const { enabled } = useListener({
    id,
    listenerFn: (data) => {
      setUuid(data.map(({ uuid }: { uuid: string }) => uuid)[0]);
      setContainer(
        data.map(({ container }: { container: string }) => container)[0]
      );
    },
    type: 'ObjectCollection',
  });
  const { theme } = useTheme('light');

  useEffect(() => {
    const interval = setInterval(() => {
      setIsRefreshing((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [refetchInterval]);

  function handleReloadToggle() {
    setRefetchInterval(refetchInterval === 0 ? 5000 : 0);
  }

  return (
    <Widget
      id={id}
      title="Object JSON"
      toolbar={
        <Button onClick={handleReloadToggle}>
          {refetchInterval > 0 ? (
            <LoadingCircle className="w-4 h-4" />
          ) : (
            <ArrowPathIcon className="w-4 h-4" />
          )}
        </Button>
      }
    >
      {enabled && data?.data && (
        <Editor
          value={data.data}
          language="json"
          theme={theme === 'light' ? 'vs-light' : 'vs-dark'}
          options={monacoEditorOptions}
        />
      )}
    </Widget>
  );
}
